import React, { useState, useEffect } from 'react';
import { List, Typography, message, Card, Layout, Collapse, DatePicker, Input, Select, Space, Tabs, Button, Steps } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { formatDistanceToNow, format } from 'date-fns';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import enUS from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import { Image } from 'antd';
const { RangePicker } = DatePicker;
const { Search } = Input;
const { TabPane } = Tabs; // 추가
const { Text } = Typography;
const { Content } = Layout;

const EmptyStateGuide = ({ handleInstallExtension }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        {
            title: t('Install Chrome Extension'),
            description: (
                <div style={{ textAlign: 'center' }}>
                    <p>{t('First, install our Chrome extension to enable meeting recording.')}</p>
                    <Button
                        type="primary"
                        onClick={handleInstallExtension}
                        style={{ marginTop: '10px', height: '40px', padding: '0 30px', fontSize: '16px' }}
                    >
                        {t('Install Chrome Extension')}
                    </Button>
                </div>
            )
        },
        {
            title: t('Go to Google Meet'),
            description: (
                <div style={{ textAlign: 'center' }}>
                    <p>{t('Visit Google Meet to start your meeting')}</p>
                    <Button
                        type="primary"
                        onClick={() => window.open('https://meet.google.com/landing', '_blank')}
                        style={{ marginTop: '10px', height: '40px', padding: '0 30px', fontSize: '16px' }}
                    >
                        {t('Open Google Meet')}
                    </Button>
                </div>
            ),
        },
        {
            title: t('Authorize Extension'),
            description: <p>{t('Click the authorize button in the top right corner of Google Meet')}</p>,
            image: '/assets/meet-auth.png'
        },
        {
            title: t('Start New Meeting'),
            description: <p>{t('Create a new Google Meet session')}</p>,
            image: '/assets/new-meeting.png'
        },
        {
            title: t('Recording Starts Automatically'),
            description: <p>{t('The meeting will be automatically transcribed, translated, and summarized')}</p>,
            image: '/assets/recording.png'
        }
    ];

    return (
        <div style={{ padding: '40px 20px', backgroundColor: '#f7f9fc' }}>
            <div style={{
                maxWidth: '1000px',
                margin: '0 auto',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#fff',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                padding: '32px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '40px',
                    padding: '0 20px'
                }}>
                    <Button
                        onClick={() => setCurrentStep(prev => prev - 1)}
                        disabled={currentStep === 0}
                        size="large"
                        icon={<LeftOutlined />}
                    >
                        {t('Previous')}
                    </Button>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {steps.map((_, index) => (
                            <div
                                key={index}
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: currentStep === index ? '#1890ff' : '#e8e8e8',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    transform: currentStep === index ? 'scale(1.2)' : 'scale(1)'
                                }}
                                onClick={() => setCurrentStep(index)}
                            />
                        ))}
                    </div>
                    <Button
                        type="primary"
                        onClick={() => setCurrentStep(prev => prev + 1)}
                        disabled={currentStep === steps.length - 1}
                        size="large"
                        icon={<RightOutlined />}
                    >
                        {t('Next')}
                    </Button>
                </div>
                <div style={{
                    display: 'flex',
                    transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: `translateX(-${currentStep * 100}%)`,
                }}>
                    {steps.map((step, index) => (
                        <div key={index} style={{
                            minWidth: '100%',
                            padding: '0 20px',
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '8px' // 24px에서 16px로 변경
                            }}>
                                <h2 style={{
                                    fontSize: '24px',
                                    fontWeight: '600',
                                    color: '#1f1f1f',
                                    marginBottom: '0' // 8px에서 0으로 변경하여 추가 간격 제거
                                }}>
                                    {step.title}
                                </h2>
                                {step.description}
                                {step.image && (
                                    <Image
                                        src={step.image}
                                        alt={step.title}
                                        style={{
                                            width: '600px',
                                            maxWidth: '100%',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};


const MyTranscription = ({ user }) => {
    const [myMeetings, setMyMeetings] = useState([]);
    const [invitedMeetings, setInvitedMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userTimeZone, setUserTimeZone] = useState('');
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState(null);
    const [sortBy, setSortBy] = useState('date-desc');
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        fetchAllMeetings();
        setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }, []);

    const fetchAllMeetings = async () => {
        setLoading(true);
        try {
            // 내가 만든 미팅 가져오기
            const { data: myData, error: myError } = await supabase
                .rpc('get_user_meetings', { input_user_id: user.id });

            if (myError) throw myError;
            setMyMeetings(myData);

            // 초대받은 미팅 가져오기
            const { data: invitedData, error: invitedError } = await supabase
                .from('invitations')
                .select(`
                    *,
                    meetings:meeting_id (*)
                `)
                .eq('target_email', user.email)
                .eq('status', 'accepted');

            if (invitedError) throw invitedError;
            setInvitedMeetings(invitedData.map(inv => inv.meetings));
        } catch (error) {
            message.error("Failed to load meeting list");
        }
        setLoading(false);
    };

    const formatDuration = (startDate, endDate) => {
        if (!endDate) return "In progress";
        const start = new Date(startDate);
        const end = new Date(endDate);
        const duration = end - start;
        const hours = Math.floor(duration / (1000 * 60 * 60));
        const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((duration % (1000 * 60)) / 1000);

        let result = [];
        if (hours > 0) result.push(`${hours}h`);
        if (minutes > 0) result.push(`${minutes}m`);
        result.push(`${seconds}s`);

        return result.join(' ');
    };


    // 필터링된 미팅 목록을 반환하는 함수
    const getFilteredMeetings = (meetings) => {
        let filtered = [...meetings];

        // 검색어 필터링
        if (searchText) {
            filtered = filtered.filter(meeting =>
                meeting.title.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        // 날짜 범위 필터링
        if (dateRange) {
            const [start, end] = dateRange;
            filtered = filtered.filter(meeting => {
                const meetingDate = new Date(meeting.created_at);
                return meetingDate >= start && meetingDate <= end;
            });
        }

        // 정렬
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'date-desc':
                    return new Date(b.created_at) - new Date(a.created_at);
                case 'date-asc':
                    return new Date(a.created_at) - new Date(b.created_at);
                case 'title':
                    return a.title.localeCompare(b.title);
                default:
                    return 0;
            }
        });

        return filtered;
    };

    const formatDate = (dateString) => {
        const date = toDate(dateString);
        return formatInTimeZone(date, userTimeZone, "yyyy-MM-dd HH:mm:ss (zzz)");
    };

    const getRelativeTime = (dateString) => {
        const date = toDate(dateString);
        return formatDistanceToNow(date, { addSuffix: true, locale: enUS });
    };

    const renderMeetingList = (meetings) => (
        <>
            {meetings.length === 0 ? (
                <EmptyStateGuide handleInstallExtension={() => {
                    window.open('https://chromewebstore.google.com/detail/eli-realtime-meeting-copi/jlcdehhmjnofkofdgelhabdfiaodiljp', '_blank');
                }} />
            ) : (
                <>
                    <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                        <Space>
                            <Search
                                placeholder={t('Search by title')}
                                onSearch={value => setSearchText(value)}
                                style={{ width: 200 }}
                            />
                            <RangePicker onChange={setDateRange} />
                            <Select
                                defaultValue="date-desc"
                                style={{ width: 120 }}
                                onChange={setSortBy}
                                options={[
                                    { value: 'date-desc', label: t('Newest') },
                                    { value: 'date-asc', label: t('Oldest') },
                                    { value: 'title', label: t('Title') },
                                ]}
                            />
                        </Space>
                    </Space>
                    <List
                        grid={{ gutter: 16, column: 1 }}
                        dataSource={getFilteredMeetings(meetings)}
                        loading={loading}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: getFilteredMeetings(meetings).length,
                            onChange: page => setCurrentPage(page),
                        }}
                        renderItem={item => (
                            <List.Item>
                                <Card
                                    hoverable
                                    onClick={() => navigate(`/meeting/${item.id}`)}
                                    style={{ width: '100%' }}
                                >
                                    <Card.Meta
                                        title={item.title}
                                        description={
                                            <>
                                                <Text>Created Date: {formatDate(item.created_at)} ({getRelativeTime(item.created_at)})</Text>
                                                <br />
                                                <Text>Status: {item.status}</Text>
                                                <br />
                                                <Text>Duration: {formatDuration(item.created_at, item.ended_at)}</Text>
                                            </>
                                        }
                                    />
                                </Card>
                            </List.Item>
                        )}
                    />
                </>
            )}
        </>
    );

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Layout>
                <Content style={{ margin: '24px 16px', padding: 24, background: '#fff' }}>
                    <Tabs defaultActiveKey="1">
                        <TabPane tab={t("My Meetings")} key="1">
                            {renderMeetingList(myMeetings)}
                        </TabPane>
                        <TabPane tab={t("Invited Meetings")} key="2">
                            {renderMeetingList(invitedMeetings)}
                        </TabPane>
                    </Tabs>
                </Content>
            </Layout>
        </Layout>
    );
};

export default MyTranscription;